import React from 'react';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import {FiBox, FiUser, FiActivity, FiAward} from 'react-icons/fi';

const features = [
  {
    icon: FiActivity,
    tag: "Medical",
    title: "Physician Reviewed & Clinically Delivered",
    description: "Independent, board-certified physicians solely focused on your care. Clinically appropriate prescription weight-loss medications are reviewed and, when indicated, shipped directly to your door with no pharmacy trips required.",
    color: "amber"
  },
  {
    icon: FiBox,
    tag: "Nutrition",
    title: "Done-For-You Nutrition",
    description: "Plug-and-play meal frameworks customized to your biology and protocol. Practical, non-restrictive guidance built for real life — no bland diet food or complicated recipes.",
    color: "stone"
  },
  {
    icon: FiUser,
    tag: "Accountability",
    title: "Iron-Clad Daily Accountability",
    description: "Direct, ongoing access to our clinical coaching team who monitor your progress daily and optimize your protocol. You won't fail because we won't let you.",
    color: "amber"
  },
  {
    icon: FiAward,
    tag: "Guarantee",
    title: "The 6-Week Results Guarantee",
    description: "Follow the nutritional plan, log your meals, and engage with your coach. If you don't see measurable progress in your weight, body composition, and energy, we work with you for free until you do.",
    color: "stone"
  },
];

const TheOffer = () => (
  <section id="the-program" className="py-20 md:py-32 bg-[#faf8f5] relative">
    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-stone-200 to-transparent" />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center max-w-3xl mx-auto mb-16 md:mb-24">
        <motion.div initial={{opacity: 0, y: 20}} whileInView={{opacity: 1, y: 0}} viewport={{once: true}}>
          <span className="inline-block px-5 py-2 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-sm font-bold uppercase tracking-widest mb-6">
            What You Get
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-stone-900 tracking-tight mb-6 font-serif leading-tight">
            Uncompromising Quality.<br />
            <span className="italic font-light text-stone-400">Unmatched Results.</span>
          </h2>
          <p className="text-xl md:text-2xl text-stone-500 font-light leading-relaxed">
            Everything you need to transform your body, curated into one seamless, medical-grade experience.
          </p>
        </motion.div>
      </div>
      <div className="grid md:grid-cols-2 gap-5 lg:gap-6">
        {features.map((feature, index) => (
          <motion.div
            key={index}
            initial={{opacity: 0, y: 30}}
            whileInView={{opacity: 1, y: 0}}
            viewport={{once: true, margin: '-50px'}}
            transition={{delay: index * 0.1, duration: 0.6}}
            className="group relative p-8 md:p-10 rounded-[2rem] bg-white border border-stone-100 hover:border-amber-200 hover:shadow-2xl hover:shadow-amber-900/5 transition-all duration-500 overflow-hidden cursor-default"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-amber-50/0 to-amber-50/0 group-hover:from-amber-50/60 group-hover:to-transparent transition-all duration-500 rounded-[2rem]" />
            <div className="relative z-10">
              <div className="flex items-start justify-between mb-6">
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${feature.color === 'amber' ? 'bg-amber-50 text-amber-600' : 'bg-stone-900 text-amber-400'} transition-all duration-500 group-hover:scale-110`}>
                  <SafeIcon icon={feature.icon} className="text-2xl" />
                </div>
                <span className="text-xs font-black uppercase tracking-widest text-stone-300 border border-stone-100 px-3 py-1 rounded-full">
                  {feature.tag}
                </span>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-stone-900 mb-4 tracking-tight font-serif leading-tight">{feature.title}</h3>
              <p className="text-lg md:text-xl text-stone-500 leading-relaxed font-light">{feature.description}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

export default TheOffer;